/**
 * ImportSourceSelector Component
 *
 * Stage 1 of the import wizard - displays import source options as radio buttons.
 * Extracted from ImportModal to support multi-stage workflow.
 *
 * @module components/import/stages
 */

import { FeatureRadio } from '@praetorian-chariot/ui';
import React from 'react';

import { ImportAction } from '@/components/import/types';

/**
 * Props for ImportSourceSelector component
 */
export interface ImportSourceSelectorProps {
  /** Available import source actions */
  actions: ImportAction[];
  /** Currently selected action value */
  selectedAction: string | null;
  /** Callback when an action is selected */
  onSelect: (action: ImportAction) => void;
}

/**
 * ImportSourceSelector Component
 *
 * Displays a list of import sources as radio buttons in a scrollable list.
 * Users select their import source before proceeding to file upload.
 *
 * @param props - Component props
 * @returns Import source selector JSX
 */
export function ImportSourceSelector({
  actions,
  selectedAction,
  onSelect,
}: ImportSourceSelectorProps) {
  return (
    <>
      <h3 className="text-chariot-text-primary text-lg font-bold">
        Select Import Source
      </h3>
      <p className="text-chariot-text-secondary text-sm">
        Select the vulnerability scanner that generated your import file.
      </p>

      {/* Scrollable radio button list */}
      <div className="scrollbar-dark max-h-[560px] space-y-3 overflow-y-auto">
        {actions.map(action => (
          <FeatureRadio
            key={action.value}
            id={action.value}
            checked={selectedAction === action.value}
            title={action.label}
            description={action.description || ''}
            onChange={() => onSelect(action)}
            disabled={action.disabled}
          />
        ))}
      </div>

      {/* Empty state when no actions available */}
      {actions.length === 0 && (
        <div className="text-chariot-text-secondary py-8 text-center">
          No import sources available
        </div>
      )}
    </>
  );
}
